import keys from 'lodash/keys';
import pickBy from 'lodash/pickBy';
import memoizeOne from 'memoize-one';
import { fg } from '@atlassian/jira-feature-gating';

type FeatureFlagMap = Record<string, boolean | string>;

const getFieldTypesFromFeatureFlagMap = (featureFlagMap: FeatureFlagMap): string[] =>
	keys(
		pickBy(featureFlagMap, (isAlwaysJsonOrFFKey: boolean | string) => {
			if (typeof isAlwaysJsonOrFFKey === 'boolean') return isAlwaysJsonOrFFKey;
			// eslint-disable-next-line jira/ff/static-feature-flags
			return fg(isAlwaysJsonOrFFKey);
		}),
	);

export const FIELD_TYPES_TO_REQUEST_AS_JSON_FF_KEYS: FeatureFlagMap = {
	assignee: true,
	'com.atlassian.servicedesk:sd-sla-field': true,
	status: true,
	issuetype: true,
	reporter: true,
	priority: true,
	summary: true,
	'com.atlassian.jira.plugins.service-entity:service-entity-field-cftype': true,
	labels: true,
	'com.atlassian.jira.plugin.system.customfieldtypes:atlassian-team': true,
	'com.atlassian.jira.plugins.cmdb:cmdb-object-cftype': true,
	'com.atlassian.jira.modules.servicemanagement.major-incident-entity:major-incident-entity-field-cftype': true,
	'com.atlassian.jira.plugins.work-category-field:work-category-field-cftype':
		'jsm_unscreenable_field_phase_2_fg',
	'com.atlassian.jira.plugin.system.customfieldtypes:multicheckboxes': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:multiselect': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:multiuserpicker': true,
	'com.atlassian.servicedesk:sd-request-participants': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:multiversion': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:multigrouppicker': true,
	'com.atlassian.servicedesk.customer-context:sd-csm-entitlement': true,
	'com.atlassian.servicedesk.sentiment:sd-sentiment': true,
	'com.atlassian.servicedesk.customer-context:sd-csm-organization': true,
	'com.atlassian.servicedesk.customer-context:sd-csm-source-channel':
		'csm_omni_source_channel_cf_fe',
	// Queue view: request additional REST-aware columns as JSON when enabled.
	created: true,
	updated: true,
	duedate: true,
	lastViewed: true,
	resolutiondate: true,
	// Queue view: next set of columns to request as JSON when enabled.
	timespent: true,
	aggregatetimespent: true,
	timeoriginalestimate: true,
	resolution: true,
	'com.atlassian.jira.plugin.system.customfieldtypes:select': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:cascadingselect': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:datepicker': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:userpicker': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:radiobuttons': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:grouppicker': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:labels': true,
	'com.atlassian.jira.plugin.system.customfieldtypes:url': true,
	'com.atlassian.servicedesk:vp-origin': true,
	'com.atlassian.servicedesk:sd-customer-organizations': true,
	issuelinks: true,
	components: true,
	creator: true,
	security: true,
	subtasks: true,
	parent: true,
};

export const getFieldTypesEligibleForJsonData: () => string[] = memoizeOne(() =>
	getFieldTypesFromFeatureFlagMap(FIELD_TYPES_TO_REQUEST_AS_JSON_FF_KEYS),
);

export const getFieldTypesEligibleForJsonDataAsSet: () => Set<string> = memoizeOne(
	() => new Set(getFieldTypesEligibleForJsonData()),
);

const FIELD_TYPES_TO_OVERRIDE_HTML_DATA_FOR_FF_KEYS: FeatureFlagMap = {
	issuekey: true,
	statusCategory: true,
};

export const getFieldTypesToOverrideHtmlDataFor: () => string[] = memoizeOne(() =>
	getFieldTypesFromFeatureFlagMap(FIELD_TYPES_TO_OVERRIDE_HTML_DATA_FOR_FF_KEYS),
);

export const getFieldTypesToOverrideHtmlDataForAsSet: () => Set<string> = memoizeOne(
	() => new Set(getFieldTypesToOverrideHtmlDataFor()),
);
